import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { Button, H3, Input, LoadingSpinner, toast } from "@sorcery/ui";

import { useUser } from "~/providers/UserProvider";
import { commentsJotai, deckJotai } from "~/state/deckJotai";
import { api, jotaiStateDelegationOptions } from "~/utils/trpc";
import { Comment } from "./Comment";

export const Comments = () => {
  const trpcUtils = api.useUtils();
  const { user } = useUser();
  const [error, setError] = useState("");
  const [parent] = useAutoAnimate();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  /************************************************************************************
   * data fetching + jotai state hydration
   */
  const [comments, setComments] = useAtom(commentsJotai);
  // available instantly due to SSG helpers
  const { isPending: commentsPending } = api.deck.getCommentsById.useQuery(
    { id: deck.id },
    {
      ...jotaiStateDelegationOptions,
      select: (data) => {
        data && setComments(data);
        return data;
      },
    },
  );

  const { mutateAsync: createComment, error: trpcError } = api.deck.createComment.useMutation({
    onSuccess: () => {
      toast("Comment added");
      setNewCommentText("");
      void trpcUtils.deck.getCommentsById.invalidate({ id: deck.id });
    },
  });

  // Track any trpc errors for display
  useEffect(() => {
    if (trpcError) {
      if (trpcError.shape?.data.code === "BAD_REQUEST") {
        setError(trpcError.shape?.message ?? "Something went wrong.");
      } else {
        // don't expose server side error messages
        setError("Something went wrong.");
      }
    }
  }, [trpcError]);

  const [newCommentText, setNewCommentText] = useState("");

  const onSave = () => {
    if (newCommentText) {
      setError("");
      void createComment({
        deckId: deck.id,
        content: newCommentText,
      });
    }
  };

  const loggedIn = !!user;
  const loading = commentsPending;
  const hasComments = !commentsPending && !!comments?.length;

  return (
    <div className="mb-4">
      <H3 className="!mb-2 hidden sm:block">Comments</H3>
      {loading ? (
        <div className="my-4 flex">
          <LoadingSpinner />
        </div>
      ) : (
        <div>
          {/* Comments */}
          {hasComments && (
            <div ref={parent}>
              {comments.map((c) => (
                <Comment comment={c} key={c.id} />
              ))}
            </div>
          )}

          <div className="w-full max-w-md">
            <Input
              placeholder="Add a new comment..."
              value={loggedIn ? newCommentText : "Sign in to comment"}
              onChange={setNewCommentText}
              disabled={!loggedIn}
            />
          </div>
          <div>
            <Button variant={"outline"} onClick={onSave} disabled={!newCommentText}>
              Submit
            </Button>
          </div>

          {error && (
            <div className="text-red my-4" role="alert">
              <p>
                <span className="font-bold">Error. </span>
                {error}
                <span>.</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
